import { useMemo } from "react";
import { useCrmContact } from "../api/crm";
import type { Message } from "../types";

interface ConversationSupportAgent {
  applicantId: number;
  active: boolean;
}

/** Newest application this thread's chat messages were sent about. */
const lastChatApplicationId = (messages: Message[]) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const applicationId = messages[i].chat_details?.application_id;
    if (applicationId) return applicationId;
  }
  return undefined;
};

/**
 * The applicant whose Application Support Agent this conversation controls: the
 * one on the application the thread's chat belongs to, else the contact's first.
 *
 * `undefined` when the contact has no applicants — there is no agent to toggle.
 * `active` defaults to true, matching the backend column's null meaning.
 */
export const useConversationSupportAgent = (
  contactId: string | undefined,
  messages: Message[]
): ConversationSupportAgent | undefined => {
  const { data: crmContact } = useCrmContact(
    contactId ? String(contactId) : ""
  );
  const chatApplicationId = useMemo(
    () => lastChatApplicationId(messages),
    [messages]
  );

  return useMemo(() => {
    const applicants = crmContact?.applicants ?? [];
    const applicant =
      applicants.find(
        (candidate) => candidate.application_id === chatApplicationId
      ) ?? applicants[0];
    if (!applicant) return undefined;
    return {
      applicantId: applicant.id,
      active: applicant.application_support_agent_active ?? true
    };
  }, [crmContact, chatApplicationId]);
};
